import { Question } from '../../types/DataTypes';

import {
  defaultAnswerForQuestionType,
  shouldShowQuestion,
} from '../../utils/assessmentUtils';

import { AssessmentResultAnswer } from '../../types';
import { AssessmentFormState } from '../../types/AssessmentTypes';

export enum ActionTypeEnum {
  EDIT_ANSWER,
  CHECK_REQUIRED_QUESTIONS,
  CLEAR_ERROR_STATES,
  CANCEL_CHANGES
}

export type ActionType =
  | {
      type: ActionTypeEnum.EDIT_ANSWER;
      updatedAnswer: AssessmentResultAnswer;
      question: Question;
    }
  | {
      type: ActionTypeEnum.CANCEL_CHANGES;
      initialFormState: AssessmentFormState;
    }
  | {
      type: ActionTypeEnum.CLEAR_ERROR_STATES;
    }
  | {
      type: ActionTypeEnum.CHECK_REQUIRED_QUESTIONS;
    }

const hideOrShowQuestions = (
  state: AssessmentFormState,
): AssessmentFormState => {
  const updatedState = {
    ...state,
  };
  // Clear answer & form field for hidden questions
  Object.keys(state).forEach((questionOrdinal) => {
    const ordinal = parseInt(questionOrdinal, 10);
    if (!shouldShowQuestion(state[ordinal].question, state)) {
      updatedState[ordinal].answer = defaultAnswerForQuestionType(
        state[ordinal].question.questionType,
      );
      updatedState[ordinal].inErrorState = false;
      updatedState[ordinal].hidden = true;
    } else {
      updatedState[ordinal].hidden = false;
    }
  });

  return updatedState;
};

const editAnswer = (
  state: AssessmentFormState,
  action: ActionType,
): AssessmentFormState => {
  if (action.type === ActionTypeEnum.EDIT_ANSWER) {
    const updatedState = {
      ...state,
    };
    // Set new answer
    updatedState[action.question.ordinal] = {
      ...state[action.question.ordinal],
      answer: action.updatedAnswer,
    };

    // Clear answer & form field for hidden questions and show questions whose
    // conditions are met.
    return hideOrShowQuestions(updatedState);
  }
  return state;
};

const checkRequiredQuestions = (
  state: AssessmentFormState,
  action: ActionType,
): AssessmentFormState => {
  const updatedState = {
    ...state,
  };

  if (action.type === ActionTypeEnum.CHECK_REQUIRED_QUESTIONS) {
    Object.keys(state).forEach((questionOrdinal) => {
      const ordinal = parseInt(questionOrdinal, 10);
      const { answer, question } = state[ordinal];
      const answerIsEmpty = !answer || !answer.answers?.length;
      updatedState[ordinal].inErrorState = !question.isOptional && answerIsEmpty;
    });
  }
  return updatedState;
};

const clearErrorStates = (
  state: AssessmentFormState,
  action: ActionType,
): AssessmentFormState => {
  if (action.type === ActionTypeEnum.CLEAR_ERROR_STATES) {
    const updatedState = {
      ...state,
    };

    Object.keys(updatedState).forEach((ordinal: string) => {
      updatedState[parseInt(ordinal, 10)].inErrorState = false;
    });
    return updatedState;
  }

  return state;
};

const cancelChanges = (
  state: AssessmentFormState,
  action: ActionType,
): AssessmentFormState => {
  if (action.type === ActionTypeEnum.CANCEL_CHANGES) {
    return action.initialFormState;
  }
  return state;
};

export const reducer = (
  state: AssessmentFormState,
  action: ActionType,
): AssessmentFormState => {
  switch (action.type) {
  case ActionTypeEnum.EDIT_ANSWER:
    return editAnswer(state, action);
  case ActionTypeEnum.CHECK_REQUIRED_QUESTIONS:
    return checkRequiredQuestions(state, action);
  case ActionTypeEnum.CLEAR_ERROR_STATES:
    return clearErrorStates(state, action);
  case ActionTypeEnum.CANCEL_CHANGES:
    return cancelChanges(state, action);
  default:
    throw new Error('Unexpected State');
  }
};
